import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentTextIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface TaxInfo {
  w9Uploaded: boolean;
  w9Url?: string;
  k1099Available: boolean;
  k1099Url?: string;
  withholding: number;
}

const TaxInformationPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<TaxInfo>({
    w9Uploaded: false,
    w9Url: '',
    k1099Available: false,
    k1099Url: '',
    withholding: 0,
  });
  const [withholding, setWithholding] = useState(0);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchTaxInfo();
  }, []);

  const fetchTaxInfo = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/tax-info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setInfo(data.info || info);
        setWithholding(data.info?.withholding || 0);
      }
    } catch (error) {
      console.error('Failed to load tax info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleW9Upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    setUploading(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('w9', e.target.files[0]);
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/tax-info/w9`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        await fetchTaxInfo();
      }
    } catch (error) {
      console.error('Failed to upload W-9:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleWithholdingChange = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/tax-info/withholding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ withholding }),
      });
      if (res.ok) {
        await fetchTaxInfo();
      }
    } catch (error) {
      console.error('Failed to update withholding:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/dashboard/settings')}
        className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
      >
        ← Back to Settings
      </button>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Tax Information</h1>
      <p className="mb-6 text-sm text-gray-600">
        Manage your tax documents and withholding settings
      </p>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">W-9 Form</h2>
        {info.w9Uploaded && info.w9Url ? (
          <div className="flex items-center space-x-3">
            <a
              href={info.w9Url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-900"
            >
              <ArrowDownTrayIcon className="w-5 h-5 inline-block mr-1" /> Download W-9
            </a>
            <span className="text-green-600 text-sm">Uploaded</span>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload W-9</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={handleW9Upload}
              disabled={uploading}
              className="block w-full text-sm text-gray-700"
            />
            <p className="mt-1 text-xs text-gray-500">PDF only. Required for US creators.</p>
          </div>
        )}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">1099-K Form</h2>
        {info.k1099Available && info.k1099Url ? (
          <a
            href={info.k1099Url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:text-indigo-900"
          >
            <ArrowDownTrayIcon className="w-5 h-5 inline-block mr-1" /> Download 1099-K
          </a>
        ) : (
          <span className="text-gray-500 text-sm">Not available yet</span>
        )}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Tax Withholding</h2>
        <div className="flex items-center space-x-3">
          <input
            type="number"
            min={0}
            max={100}
            value={withholding}
            onChange={(e) => setWithholding(Number(e.target.value))}
            className="w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
          />
          <span className="text-sm text-gray-700">%</span>
          <button
            onClick={handleWithholdingChange}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
          >
            Update
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Set your tax withholding percentage (0-100%)
        </p>
      </div>
    </div>
  );
};

export default TaxInformationPage;
